import React, { useState, useEffect } from "react"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import Tooltip from "aldoo-ra/Tooltip"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { showNotification } from "aldoo-ra/Notification"
import { ArrowUturnLeftIcon, TrashIcon, EyeIcon } from "@heroicons/react/24/outline"
import ContentEditor from "aldoo-ra/CMS/content-editor/index"
import TypeResolver from "aldoo-ra/CMS/type-resolver"

import { GetCMSContent, GetCMSType } from "../api"

export default function RestoreDialog({ controller, isOpen, onClose, type, additionalFilters = {} }) {
  const [refreshKey, setRefreshKey] = useState(false)
  const [previewItem, setPreviewItem] = useState(null)
  const [showPreview, setShowPreview] = useState(false)
  const [currentItems, setCurrentItems] = useState([])
  const [deletedCount, setDeletedCount] = useState(0)
  // If no type is provided, return null
  if (!type) return null

  const { restoreContent, restoreType, deleteContent, deleteType } = controller
  const { getContentType } = TypeResolver()

  const performRestore = type === "content" ? restoreContent : restoreType
  const performDelete = type === "content" ? deleteContent : deleteType
  const fetchTableData = type === "content" ? GetCMSContent : GetCMSType
  const searchField = type === "content" ? "content.name" : "content.label"

  useEffect(() => {
    // Refresh the table when necessary
    controller.onRefreshContents(() => setRefreshKey((prev) => !prev))
  }, [])

  // Fetch deleted items count
  useEffect(() => {
    const fetchDeletedItems = async () => {
      if (!isOpen) return

      try {
        const response = await fetchTableData({
          match: { deleted: true, ...additionalFilters },
          itemsPerPage: 9999 // Get all items to count them
        })

        if (response.result && response.data) {
          setCurrentItems(response.data)
          setDeletedCount(response.data.length)
        }
      } catch (error) {
        console.error('Failed to fetch deleted items:', error)
      }
    }

    fetchDeletedItems()
  }, [isOpen, refreshKey, type])

  //refresh the table on open
  useEffect(() => {
    if (!isOpen) return
    setRefreshKey((prev) => !prev)
  }, [isOpen])

  // Handler for deleting an item forever
  const deleteForeverAction = (item) => {
    showPromptDialog({
      title: "Delete Forever",
      message: `Are you sure you want to delete "${item?.content?.name || item?.content?.label || 'this item'}" forever? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const result = await performDelete(item._id, "hard") // Call deleteContent with mode set to 'hard'
          
          // Check if delete was successful
          if (result && !result.error) {
            showNotification({
              message: `${item?.content?.name || item?.content?.label || 'Item'} deleted permanently`,
              className: "bg-admin_primary text-white",
            })
            
            // Force immediate refresh to remove the deleted item from the list
            setRefreshKey((prev) => !prev)
            
            // Additional refresh after a short delay to ensure database consistency
            setTimeout(() => {
              setRefreshKey((prev) => !prev)
            }, 150)
          } else {
            throw new Error(result?.error || "Delete failed")
          }
        } catch (error) {
          console.error("Delete error:", error)
          showNotification({
            message: "Failed to delete the item.",
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handler for restoring an item
  const restoreAction = async (item) => {
    try {
      const result = await performRestore(item._id) // Restore content

      // Check if restore was successful
      if (result && !result.error) {
        // Show success notification
        showNotification({
          message: `${item?.content?.name || item?.content?.label || 'Item'} restored successfully`,
          className: "bg-admin_primary text-white",
        })

        // Force immediate refresh to remove the restored item from the list
        // Use multiple refresh triggers to ensure the update is visible
        setRefreshKey((prev) => !prev)

        // Additional refresh after a short delay to ensure database consistency
        setTimeout(() => {
          setRefreshKey((prev) => !prev)
        }, 150)
      } else {
        throw new Error(result?.error || "Restore failed")
      }
    } catch (error) {
      console.error("Restore error:", error)
      showNotification({
        message: "Failed to restore the item.",
        className: "bg-admin_error text-white",
      })
    }
  }

  // Handler for deleting all items forever
  const deleteAllAction = async (items) => {
    if (!items || items.length === 0) {
      showNotification({
        message: "No items to delete",
        className: "bg-admin_error text-white",
      })
      return
    }

    showPromptDialog({
      title: "Delete All Forever",
      message: `Are you sure you want to permanently delete all ${items.length} item${items.length !== 1 ? 's' : ''}? This action cannot be undone and all content will be permanently deleted.`,
      confirmText: "Delete All",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          let deletedCount = 0
          let failedCount = 0

          for (const item of items) {
            try {
              const result = await performDelete(item._id, "hard")
              if (result && !result.error) {
                deletedCount++
              } else {
                failedCount++
              }
            } catch (error) {
              console.error(`Failed to delete item ${item._id}:`, error)
              failedCount++
            }
          }

          if (deletedCount > 0) {
            showNotification({
              message: `Permanently deleted ${deletedCount} item${deletedCount !== 1 ? 's' : ''}${failedCount > 0 ? `, ${failedCount} failed` : ''}`,
              className: "bg-admin_primary text-white",
            })
          } else {
            showNotification({
              message: "Failed to delete items",
              className: "bg-admin_error text-white",
            })
          }

          // Refresh the table
          setRefreshKey((prev) => !prev)
          setTimeout(() => {
            setRefreshKey((prev) => !prev)
          }, 150)
        } catch (error) {
          console.error("Delete all error:", error)
          showNotification({
            message: "Failed to delete items",
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handler for previewing an item
  const previewAction = (item) => {
    // Validate item has required structure
    if (!item || !item.content) {
      showNotification({
        message: "Cannot preview item: Missing content information",
        className: "bg-admin_error text-white",
      })
      return
    }
    
    // For regular content items, require type field
    // For ContentType items (type === "type"), the type field is not required since they ARE the type definition
    if (type !== "type" && !item.content.type) {
      showNotification({
        message: "Cannot preview item: Missing content type information",
        className: "bg-admin_error text-white",
      })
      return
    }
    
    setPreviewItem(item)
    setShowPreview(true)
  }

  // Define columns for the data table
  const tableItemRender = [
    {
      id: "type",
      title: "Type",
      headerClassName: "w-[18%]",
      render: (value, item) => (
        <span className="text-admin_text text-sm truncate block">
          {item?.content?.type || 'Unknown'}
        </span>
      ),
    },
    {
      id: "label",
      title: "Name / Label",
      headerClassName: "w-[42%]", 
      render: (value, item) => (
        <div className="truncate max-w-full">
          <span className="text-admin_text text-sm truncate block">
            {item?.content?.label || item?.content?.name || 'Untitled'}
          </span>
        </div>
      ),
    },
    {
      id: "preview",
      title: "Preview",
      headerClassName: "w-[15%] text-center",
      render: (value, item) => (
        <Tooltip content="Preview content details" position="top">
          <EyeIcon 
            className="w-6 h-6 text-admin_text hover:text-admin_primary cursor-pointer mx-auto" 
            onClick={() => previewAction(item)}
          />
        </Tooltip>
      ),
    },
    {
      id: "restore",
      headerClassName: "w-[12.5%] text-center",
      render: (value, item) => (
        <Tooltip content="Restore this item" position="top">
          <ArrowUturnLeftIcon 
            className="w-6 h-6 text-admin_text hover:text-admin_primary cursor-pointer mx-auto" 
            onClick={() => restoreAction(item)}
          />
        </Tooltip>
      ),
    },
    {
      id: "delete",
      headerClassName: "w-[12.5%] text-center",
      render: (value, item) => (
        <Tooltip content="Delete permanently" position="top">
          <TrashIcon 
            className="w-6 h-6 text-admin_text hover:text-admin_error cursor-pointer mx-auto" 
            onClick={() => deleteForeverAction(item)}
          />
        </Tooltip>
      ),
    },
  ]

  return (
    <Modal
      variant="admin"
      isOpen={isOpen}
      onClose={onClose}
      closeOnEscape
      className="md:absolute w-full md:w-[90%] lg:w-[80%] xl:w-[70%] h-full md:h-auto top-[15px]"
      zIndex={200}
    >
      <div className="p-4">
        <Typography variant="admin_h3" className="mb-4">
          {type === "type" ? "Restore Types" : "Restore Content"}
        </Typography>

        {deletedCount > 0 && (
          <div className="flex justify-center mb-4">
            <Button
              variant="admin_error"
              onClick={() => deleteAllAction(currentItems)}
              className="flex items-center gap-2"
            >
              <TrashIcon className="w-4 h-4" />
              Delete All ({deletedCount})
            </Button>
          </div>
        )}

        {/* Data table for displaying deleted items */}
        <div className="h-[70vh] overflow-hidden">
          <DataTable
            variant="admin"
            refreshKey={refreshKey}
            apiMethod={fetchTableData}
            preferredHeight="55vh"
            itemHeight="h-[50px]"
            showFooter={true}
            showSearch={true}
            localUserPreferencesKey="cms.restore"
            styleOverrides={{
              searchInput: "ml-10 flex-1 w-[250px] mt-4",
              table: "w-full table-fixed",
              container: "overflow-x-hidden",
              tableWrapper: "h-[45vh] flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper",
              getRowStyle: (index) =>
                index % 2 === 0
                  ? "bg-admin_paper hover:bg-admin_neutral_1 transition-colors duration-200 cursor-pointer"
                  : "bg-admin_canvas hover:bg-admin_neutral_1 transition-colors duration-200 cursor-pointer"
            }}
            searchField={searchField} // Field to search
            requestFields={{ match: { deleted: true, ...additionalFilters } }} // Only fetch deleted items with additional filters
            searchObject={{}} // Search object
            sort={{ deletedAt: -1 }} // Sort by deletion date (descending)
            renders={tableItemRender} // Columns definition
            noData={
              <div className="min-h-[300px] flex items-center justify-center">
                <Typography variant="paragraph" className="text-admin_text">
                  No deleted content yet
                </Typography>
              </div>
            }
          />
        </div>
      </div>

      {/* Preview Modal */}
      <Modal
        variant="admin"
        isOpen={showPreview}
        onClose={() => {
          setShowPreview(false)
          setPreviewItem(null)
        }}
        closeOnEscape
        className="md:absolute w-full md:w-[80%] lg:w-[70%] xl:w-[60%] h-full md:h-auto top-[15px]"
        zIndex={300}
      >
        <div className="p-4">
          {previewItem && previewItem.content ? (
            <div className="space-y-4">
              {/* Content Editor */}
              <div className="max-h-[60vh] overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper">
                {(() => {
                  // Special handling for ContentType items (when type === "type")
                  let contentType;
                  if (type === "type") {
                    // For ContentType items, use the ContentType definition itself
                    contentType = getContentType("ContentType");
                  } else {
                    // For regular content items, resolve by their type
                    contentType = getContentType(previewItem?.content?.type);
                  }
                  
                  // Check if contentType exists and has valid structure
                  if (contentType && typeof contentType === 'object' && (contentType.fields || contentType.groups)) {
                    return (
                      <ContentEditor
                        title={`Preview: ${previewItem?.content?.name || previewItem?.content?.label || 'Untitled'}`}
                        contentType={contentType}
                        dataItem={previewItem}
                        fieldValues={{
                          ...(previewItem?.content ? (() => {
                            const { type, ...contentFields } = previewItem.content;
                            return contentFields;
                          })() : {}),
                          _id: previewItem?._id,
                        }}
                        variant="admin"
                        clearValuesAfterSave={false}
                        // Read-only mode - no save functionality
                        onSave={null}
                        onClickSave={null}
                        // Hide Copy/Paste/Export buttons in preview mode
                        showCopyPaste={false}
                        showExport={false}
                      />
                    );
                  } else {
                    return (
                      <div className="bg-admin_canvas rounded-lg p-4">
                        <Typography 
                          variant="admin_txt2" 
                          className="font-semibold mb-2"
                        >
                          {type === "type" 
                            ? "ContentType definition not found or has no fields. Raw content:"
                            : contentType 
                              ? `Content Type "${previewItem?.content?.type}" has no fields or groups defined. Raw content:` 
                              : `Content Type "${previewItem?.content?.type}" not found. Raw content:`
                          }
                        </Typography>

                        <pre className="text-admin_text text-sm whitespace-pre-wrap break-words">
                          {JSON.stringify(previewItem?.content || {}, null, 2)}
                        </pre>
                      </div>
                    );
                  }
                })()}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-admin_bordered">
                <Button
                  variant="admin_primary"
                  onClick={() => {
                    restoreAction(previewItem)
                    setShowPreview(false)
                    setPreviewItem(null)
                  }}
                  className="flex items-center gap-2"
                >
                  <ArrowUturnLeftIcon className="w-4 h-4" />
                  Restore This Item
                </Button>

                <Button
                  variant="admin_secondary"
                  onClick={() => {
                    setShowPreview(false)
                    setPreviewItem(null)
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-admin_canvas rounded-lg p-4">
              <Typography   
                variant="admin_txt2" 
                className="text-center"
              >
                No content available to preview
              </Typography>
            </div>
          )}
        </div>
      </Modal>
    </Modal>
  )
}
